<script setup lang="ts">
import { ref, onMounted, nextTick } from 'vue'
import { useStore } from 'vuex'
import mastercard from '@/assets/payment/mastercard.svg'
import visa from '@/assets/payment/visa.svg'
import amex from '@/assets/payment/amex.svg'
import unionpay from '@/assets/payment/unionpay.svg'
import lock from '@/assets/payment/lock.svg'
import AdyenTestCards from '@/components/Payments/AdyenTestCards.vue'
import { usePaymentPlan } from '@/composables/paymentPlan'
import { useFetch } from '@/composables/fetch'
import {
  AdyenCheckout,
  CustomCard,
  type CoreConfiguration,
  type PaymentFailedData,
  type PaymentCompletedData,
} from '@adyen/adyen-web'
import GwBtn from '@/components/lib/GwBtn/GwBtn.vue'
import { t } from '@/locales/i18n'
import { updateSubscription, segmentTrackSuccess } from '@/helpers/paymentHelpers'
import { seg_track } from '@/helpers/segment'
import type { PaymentPlan } from '@/types/paymentPlans'
import PaymentLoader from '@/components/Payments/PaymentLoader.vue'
import AdyenPaymentErrors from '@/components/Payments/AdyenPaymentErrors.vue'
import { logSentry } from '@gto/sentry'
import { adyenConfirmResult } from '@gto/api/vue-query'

const {
  selectedPlan,
  selectedTierPlan,
  upgradingCurrentPlan,
  currentSubscription,
  currentTierPlan,
} = usePaymentPlan()
const { fetch } = useFetch()
const store = useStore()

// Props
interface AdyenGatewayProps {
  safeBadges: boolean
  onSuccessPayment: (params: { payment_provider: string; payment_method: string }) => void
  singlePayment: boolean
  customPlan?: PaymentPlan
}

const props = defineProps<AdyenGatewayProps>()
const emit = defineEmits(['payment-done'])

interface CardDetails {
  cardNumber: string | boolean
  cardExpiry: string | boolean
  cardCvc: string | boolean
}
interface AdyenError {
  error: boolean
  message?: string
  status?: string
}
interface FormCreationErrorParams {
  error: object | string
  endpoint?: string
}

const paymentForm = ref<HTMLElement | null>(null)
const formCreationError = ref<FormCreationErrorParams | null>(null)
const customCard = ref<CustomCard | null>(null)
const localLoading = ref(false)
const updatingSubscription = ref(false)
const updateSubscriptionTimedOut = ref(false)
const threeDSChallenge = ref(false)
const canCheckout = ref(false)
const formLoaded = ref(false)
const errors = ref<CardDetails>({
  cardNumber: '',
  cardExpiry: '',
  cardCvc: '',
})
const adyenError = ref<AdyenError>({
  error: false,
})

const productionENV =
  import.meta.env.VITE_ENV === 'production' && import.meta.env.VITE_BRANCH === 'master'

const handlePaymentFailure = async (error: AdyenError) => {
  await store.dispatch('globals_action_reset')
  localLoading.value = false
  adyenError.value = error
  logSentry('Adyen Gateway: Payment failed', {
    error: JSON.stringify(error, null, 2),
    email: store.getters.user?.email,
    selectedPlan: selectedTierPlan.value?.full_name,
    currentPlan: currentTierPlan.value?.full_name,
  })
  resetCardForm()
}

const handlePaymentSuccess = async (
  result: PaymentCompletedData,
  plan: PaymentPlan,
  sessionId: string,
) => {
  if (result.resultCode !== 'Authorised') {
    const message = result.resultCode === 'Refused' ? 'Payment was refused!' : result.resultCode
    handlePaymentFailure({ error: true, message, status: 'CARD_DECLINED' })
    return
  }
  if ('sessionResult' in result && typeof sessionId === 'string') {
    // This request is needed for immediate confirmation of the payment.
    adyenConfirmResult({ session_id: sessionId, session_result: result.sessionResult })
  }

  emit('payment-done')
  updatingSubscription.value = true
  updateSubscriptionTimedOut.value = false
  localStorage.setItem('gtm_payment_session', Date.now().toString())
  updateSubscription(
    plan,
    'upgrade',
    () => {
      updatingSubscription.value = false
      segmentTrackSuccess(selectedTierPlan.value, 'credit_card')
      props.onSuccessPayment({
        payment_provider: 'adyen',
        payment_method: 'card',
      })
    },
    () => {
      updateSubscriptionTimedOut.value = true
    },
  )
}

const createError = (error: object, endpoint: string) => {
  formCreationError.value = {
    error: Object.keys(error).length ? error : t('payment.errors.create_form'),
    endpoint,
  }
}

const createPaymentMethod = async () => {
  if (!canCheckout.value) {
    return
  }
  await store.dispatch('globals_action', 'processing-payment')
  localLoading.value = true
  adyenError.value = { error: false }
  seg_track('payment_info_entered', {
    checkout_id: window.seg_checkout_id,
  })
  await nextTick(() => {
    if (!customCard.value) {
      adyenError.value = { error: true, message: t('payment.errors.create_payment') }
      return
    }
    customCard.value.submit()
  })
}

const resetAdyenError = () => {
  adyenError.value = { error: false }
}

const initialize = async () => {
  const plan = props.singlePayment ? props.customPlan : selectedPlan.value

  if (!plan?.id) {
    formCreationError.value = { error: 'payment.error.no_plan_selected' }
    logSentry('Adyen Gateway: Form creation error', {
      error: 'payment.error.no_plan_selected',
      email: store.getters.user?.email,
      selectedPlan: selectedTierPlan.value?.full_name,
      currentPlan: currentTierPlan.value?.full_name,
    })
    return
  }

  const endpoint = props.singlePayment
    ? `/payments/adyen/credits/`
    : `payments/subscription/adyen/${upgradingCurrentPlan.value ? `${currentSubscription.value!.public_id}/` : ''}`

  const method = props.singlePayment ? 'post' : upgradingCurrentPlan.value ? 'patch' : 'post'

  const postData = props.singlePayment
    ? {
        credit_amount: plan.credits,
        country_code: 'US',
        return_url: `${window.location.origin}/credits`,
        use_card: false,
      }
    : {
        plan: plan.id,
        use_card: false,
        country_code: 'US',
      }

  const { data, error } = await fetch({
    conf: {
      endpoint,
      apiVersion: props.singlePayment ? 'v4' : 'v1',
      method,
    },
    data: postData,
  })

  if (error || !data) {
    createError(error, endpoint)
    return
  }

  const { ext_id, session_data } = data
  if (!ext_id || !session_data) {
    createError(error, endpoint)
    return
  }

  const config: CoreConfiguration = {
    locale: 'en_US',
    countryCode: 'US',
    environment: productionENV ? 'live' : 'test',
    clientKey: import.meta.env.VITE_ADYEN_WEB_KEY,
    analytics: { enabled: false },
    session: {
      id: ext_id,
      sessionData: session_data,
    },
    amount: {
      value: plan.price || 0,
      currency: 'USD',
    },
    onPaymentCompleted: (result: PaymentCompletedData) =>
      handlePaymentSuccess(result, plan, ext_id),
    onPaymentFailed: (err: PaymentFailedData) => {
      const message = err.resultCode === 'Refused' ? 'Payment was refused!' : err.resultCode
      handlePaymentFailure({ error: true, message, status: 'UNKNOWN_ERROR' })
    },
  }
  const checkout = await AdyenCheckout(config)
  const formStyle = {
    lineHeight: '16px',
    color: 'black',
    background: '#f5f5f5 border-box !important',
    fontFamily: 'sans-serif',
    fontSize: '14px',
    fontWeight: '400',
  }
  if (!paymentForm.value) {
    formCreationError.value = { error: 'No form element found' }
    return
  }

  const brands = import.meta.env.VITE_ADYEN_WEB_BRANDS.split(',')
  customCard.value = new CustomCard(checkout, {
    styles: {
      base: formStyle,
      placeholder: {
        ...formStyle,
        color: 'grey',
      },
    },
    challengeWindowSize: '05',
    placeholders: {
      cardNumber: '1234 1234 1234 1234',
      expiryDate: 'MM / YY',
      securityCodeThreeDigits: 'CVC',
      securityCodeFourDigits: 'CVC',
    },
    onActionHandled: async (actionHandled: any) => {
      await store.dispatch('globals_action_reset')
      localLoading.value = false
      threeDSChallenge.value = true
    },
    onLoad: () => {
      formLoaded.value = true
    },
    onEnterKeyPressed: () => {
      if (formLoaded.value) {
        createPaymentMethod()
      }
    },
    onChange: (state: any) => {
      if (formLoaded.value) {
        resetAdyenError()
      }
      canCheckout.value = state.isValid
      errors.value = {
        cardNumber: state.errors?.encryptedCardNumber?.errorI18n || '',
        cardExpiry:
          state.errors?.encryptedExpiryDate?.errorI18n ||
          state.errors?.encryptedExpiryYear?.errorI18n ||
          state.errors?.encryptedExpiryMonth?.errorI18n ||
          '',
        cardCvc: state.errors?.encryptedSecurityCode?.errorI18n || '',
      }
    },
    brands,
  }).mount(paymentForm.value)
}

/**
 * Resets the card component after 3DS challenge so the user can try again after a failed payment.
 */
function resetCardForm() {
  if (threeDSChallenge.value) {
    threeDSChallenge.value = false
    formLoaded.value = false
    initialize()
  }
}

onMounted(() => {
  initialize()
})
</script>

<template>
  <div class="adyen-box anim-fade-in">
    <AdyenPaymentErrors
      :form-creation-error="formCreationError"
      :adyen-error="adyenError"
    />

    <div
      ref="paymentForm"
      class="anim-fade-in adyen-form-container"
      :style="threeDSChallenge ? 'height: 600px' : ''"
    >
      <div
        v-if="!threeDSChallenge"
        v-show="formLoaded && !updatingSubscription"
      >
        <div class="position-relative">
          <div class="input-cards">
            <unionpay class="mr-1" />
            <visa class="mr-1" />
            <amex class="mr-1" />
            <mastercard class="mr-1" />
            <div class="csst f-center icon-lock">
              <lock class="mr-1 no-events" />
              <div
                class="csst_content text-nowrap"
                style="top: -2rem; right: 0"
              >
                {{ $t('tooltips.lock_ecrypted') }}
              </div>
            </div>
          </div>
          <span class="label-text">{{ $t('payment.credit_card_number') }}</span>
          <div
            id="card-number"
            data-tst="card_number"
            class="adyen-input"
            data-cse="encryptedCardNumber"
            :class="{ 'error-input': errors.cardNumber }"
          ></div>
          <div
            v-if="errors.cardNumber"
            data-tst="error_cardNumber"
            class="adyen-error"
          >
            {{ t('payment.errors.invalid_card_number') }}
          </div>
        </div>

        <div class="halved mt-4">
          <div>
            <span class="label-text">{{ $t('payment.credit_card_exiration') }} </span>
            <div
              id="card-expiry"
              data-tst="card_expiry"
              class="adyen-input"
              data-cse="encryptedExpiryDate"
              :class="{ 'error-input': errors.cardExpiry }"
            ></div>
            <div
              v-if="errors.cardExpiry"
              class="adyen-error"
              data-tst="error_cardExpiry"
            >
              {{ t('payment.errors.invalid_expiry') }}
            </div>
          </div>
          <div>
            <span class="label-text">{{ $t('payment.credit_card_cvc') }}</span>
            <div
              id="card-cvc"
              data-tst="card_cvc"
              class="adyen-input"
              data-cse="encryptedSecurityCode"
              :class="{ 'error-input': errors.cardCvc }"
            ></div>
            <div
              v-if="errors.cardCvc"
              class="adyen-error"
              data-tst="error_cardCvc"
            >
              {{ t('payment.errors.invalid_cvc') }}
            </div>
          </div>
        </div>

        <div
          v-if="!canCheckout"
          class="mt-8 plan-box_btn cursor-not-allowed"
        >
          <GwBtn
            v-if="formLoaded"
            variant="btn_text_grey_white"
            data-tst="btn_cannot_check_out"
            class="checkout-button gw_btn w-100 no-events uppercase justify-center"
          >
            <template #default>
              {{ $t('payment.check_out') }}
            </template>
          </GwBtn>
        </div>

        <div
          v-else
          class="mt-8 plan-box_btn anim-slide-shine"
        >
          <GwBtn
            variant="btn_primary"
            loadingVariant="spinner"
            data-tst="btn_check_out"
            class="w-100 uppercase justify-center"
            :text="$t('payment.check_out')"
            :loading="localLoading"
            style="
              font-size: 14px;
              font-family: 'Inter', sans-serif;
              height: 44px;
              letter-spacing: 1.5px;
            "
            @click="createPaymentMethod"
          />
        </div>

        <slot
          name="text"
          :tierPlan="selectedTierPlan"
        />

        <div
          v-if="safeBadges"
          class="safe-secure-checkout mt-8"
        >
          <a
            target="_blank"
            href="https://letsencrypt.org/"
            class="d-flex"
          >
            <div class="mr-2 mdi mdi-lock"></div>
            {{ $t('payment.safe_secure_checkout') }}
          </a>
        </div>
      </div>
      <PaymentLoader
        :is-loading="!formLoaded"
        :is-updating="updatingSubscription"
        :is-timed-out="updateSubscriptionTimedOut"
        :selected-plan="selectedPlan"
        :form-creation-error="Boolean(formCreationError)"
      />
    </div>

    <AdyenTestCards v-if="!productionENV && !threeDSChallenge && !updatingSubscription" />
  </div>
</template>

<style lang="scss">
@import '@adyen/adyen-web/styles/adyen.css';
</style>

<style lang="scss" scoped>
.checkout-button {
  height: 44px;
  font-size: 1rem;
  letter-spacing: 1.2px;
  color: white;
  --surface-ct-opacity: 0.5;
  --surface-ct-bg: var(--clr-grey-2);
  --text-clr: var(--clr-black-1);
}
.adyen-input {
  height: 3.2rem;
}
.adyen-form-container {
  height: 400px;
}
.adyen-box {
  padding-bottom: 2rem;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}
.input-cards {
  position: absolute;
  z-index: 4;
  top: 2.5rem;
  right: 0.8rem;
  display: flex;
  align-items: center;
  justify-content: center;
}
#card-number,
#card-expiry,
#card-cvc {
  padding: 1rem;
  margin-top: 0.25rem;
  background: var(--clr-white-0);
  border-radius: 8px;
}
.halved {
  display: grid;
  grid-template-columns: 1fr 1fr;
  grid-gap: 1rem;
}
.adyen-error {
  padding-top: 4px;
  font-size: 1rem;
  color: var(--clr-error);
}
.error-input {
  box-shadow: 0px 0px 0px 1px var(--clr-error);
}
.safe-secure-checkout {
  display: flex;
  a {
    color: #90caf9 !important;
  }
  align-items: center;
  justify-content: center;
}
.svg-badges {
  background: white;
  border-radius: 99px;
  overflow: hidden;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: center;
  padding: 4px 0;
  &_svgs {
    min-width: 16rem;
    width: 16rem;
    display: flex;
    align-items: center;
    justify-content: center;
  }
}
.svg-badge {
  position: relative;
  background: white;
  display: flex;
  align-items: center;
  justify-content: center;
  svg {
    position: absolute;
    z-index: 2;
  }
}

@media (max-width: 767px) {
  .svg-badges {
    flex-direction: column;
    // width: 100%;
    border-radius: 99px;
    padding: 4px 1rem;
  }
  .input-cards {
    top: -3px;
    right: 0px;
    padding: 2px 6px;

    // background: #9e9e9e;
    border-radius: 8px;
  }
  .b-discover {
    background: #9e9e9e;
  }
  .icon-lock {
    position: absolute;
    top: 38px;
    right: 12px;
  }
  #card-number,
  #card-expiry,
  #card-cvc {
    height: 44px;
  }
}
</style>
